import { createSelector } from '@reduxjs/toolkit';

import { RootState } from './store';
import { Composition, OptionValues } from './types/composition';
import { CfgQuestion, Part } from './types/catalog-types';
import { CatalogRoot, Material } from './types/catalog-internal';
import { conditionApplies, optionsConditionApplies } from './conditions';
import { contentSelector, implicitValuesByGroupSelector, selectedElementsSelector, selectedGroupSelector, selectedImplicitValuesSelector, selectedValuesSelector } from './configurator-slice';
import { calculateCountersUse } from './configurator-helpers';
import { resolveAttachControlsInfo } from './composition-helpers';

const MATERIAL_NAMES_OMIT_QUALITY_IF = [ 'piping' ];


// ==== Primitive selectors (just select one piece of state)

// - chosen configurator (which category/family/variant)
const categorySelector = (state: RootState) => state.configurator.category;
const familySelector = (state: RootState) => state.configurator.family;
const variantSelector = (state: RootState) => state.configurator.variant;


// - position in questions
const currentQuestionSelector = (state: RootState) => state.configurator.currentQuestion;
const currentNStepsSelector = (state: RootState) => state.configurator.currentNSteps;
const currentStepSelector = (state: RootState) => state.configurator.currentStep;
const questionDataSelector = (state: RootState) => state.configurator.questionData;


// --- catalog
export const catalogSelector = (state: RootState) => state.configurator.catalog;
export const catalogReadySelector = (state: RootState) => state.configurator.catalog;


// --- values



// ==== Composed selectors

// ---- trivial but ofter used
export const hasComposeSelector = createSelector(catalogSelector, 
                                                 catalog => catalog.compose );



// ---- chosen family/variant and catalog related

export const chosenConfiguratorSelector = createSelector(
    categorySelector,
    familySelector,
    variantSelector,
    (categoryId, familyId, variantId) => ({
        categoryId,
        familyId,
        variantId
    })
)

export const materialNamesSelector = createSelector(
    catalogSelector,
    selectedGroupSelector,
    selectedImplicitValuesSelector,
    (catalog, selectedGroup, values) => {

        const summaryProps = catalog.summaryProperties ?? [];
        const added = new Set<string>();
        const summaryQualities: string[] = [];
        
        function addQuality(qualityId: string, colorName: string) {

            const quality = catalog.qualities.find(q => q.code === qualityId);
            const name = `${quality?.name ?? ''} ${colorName}`;

            if(!added.has(name)) {
                summaryQualities.push(name);
                added.add(name);
            }
        }

        for(const element of selectedGroup?.elements ?? []) {

            const catalogElementParts = catalog.elementParts.filter(ep => {
                if(ep.element !== element.element) {
                    return false;
                } else {
                    if(ep.showif) {
                        return conditionApplies(ep.showif, values, catalog.qualities, []);
                    } else {
                        return true;
                    }
                }
            })

            for(const catElementPart of catalogElementParts) {

                const part = catalog.parts.find(p => p.id === catElementPart.part);
                if(!part) continue;

                for(const meshRule of (part.meshes ?? [])) {
                    if(meshRule.material && summaryProps.includes(meshRule.material)) {
                        const propValue = values[meshRule.material];
                        if(!propValue) continue;

                        const [ qualityId, colorId ] = propValue.split('/');
                        const color = catalog.colors.find(c => c.material === qualityId && c.code === colorId);
                        if(!color) continue;

                        if(meshRule.useLeatherAccent && color.leatherAccent) {
                            const leatherAccentColor = catalog.colors.find(c => c.colorId === color.leatherAccent);
                            if(leatherAccentColor) {
                                addQuality(leatherAccentColor.material, leatherAccentColor.name);
                            }
                        } else {
                            addQuality(qualityId, color.name);
                        }
                    }
                }

            }
        }
    
        return summaryQualities.map(text => text?.replaceAll(/\s+/g, '\xa0')).join(' / ');  
    

    }
)

export const jrModelSelector = createSelector(
    catalogSelector,
    ({properties}) => {
        const modelProperty = properties.find(p => p.name === 'jrmodel');
        return modelProperty?.initial;    
    }
)


// ---- questions

export const questionPositionSelector = createSelector(
    currentQuestionSelector,
    currentNStepsSelector,
    currentStepSelector,
    questionDataSelector,
    (question, nSteps, step, questionData) => [ question, nSteps, step, questionData ]
);

export const suitableQuestionsSelector = createSelector(
    selectedImplicitValuesSelector,
    selectedElementsSelector,
    contentSelector,
    catalogSelector,
    (values, selectedElements, composition, catalog) => {
        // Use implicit values so showif on covering tags works with covering defaults
        // (covering0 often has default but no initial / explicit value yet).
        return calculateSuitableQuestions(values, composition, catalog, selectedElements);
    }
)

export const stagesSelector = createSelector(
    suitableQuestionsSelector,
    catalogSelector,
    ([ questions ], { stages }) => stages.filter(s => questions.some(q => q.stage === s.id))
)

// this is a function and not part of suitableQuestionsSelector (above) because it is also used in the handlers of next/prev
export function calculateSuitableQuestions( values: OptionValues, composition: Composition, catalog: CatalogRoot, selectedElements: string[] ): [ CfgQuestion[], number, number] {
    const sOptions = catalog.questions.filter(q => optionsConditionApplies(q.showif, values, composition, catalog, selectedElements));
    const min = sOptions.reduce((min, q) => Math.min(min, q.order), Number.MAX_SAFE_INTEGER);
    const max = sOptions.reduce((max, q) => Math.max(max, q.order), -1);
    return [ sOptions, min, max ];
}


// ---- values ----







// ---- counters ----

export const countersUseSelector = createSelector(
    contentSelector,
    catalogSelector,
    implicitValuesByGroupSelector,
    (composition, catalog, implicitValuesByGroup) => {
        return calculateCountersUse(composition, catalog, implicitValuesByGroup);
    }
);



// ---- UI related ----

// indicates if there's some attachment pending ()
export const requiredAttachPendingSelector = createSelector(
    contentSelector,
    catalogSelector,
    (composition, catalog) => {
        const attachPoints = resolveAttachControlsInfo(composition.groups, catalog.elements);
        return attachPoints.some(ap => ap.type == 'free-required');
    }
)